import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentTasks, setRecentTasks] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const [budgetData, setBudgetData] = useState(null);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, tasksRes, agentsRes, budgetRes] = await Promise.all([
        axios.get('/api/users/stats'),
        axios.get('/api/tasks?limit=5'),
        axios.get('/api/agents'),
        axios.get('/api/budget/savings').catch(() => ({ data: { savings: null } }))
      ]);

      setStats(statsRes.data.stats);
      setRecentTasks(tasksRes.data.tasks);
      setAgents(agentsRes.data.agents);
      setBudgetData(budgetRes.data.savings);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div>
        <h1 className="text-3xl font-bold mb-2">
          Welcome back, {user?.name?.split(' ')[0]}! 👋
        </h1>
        <p className="text-gray-400">
          Here's what's happening with your AI agents today.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard
          title="Active Agents"
          value={stats?.activeAgents || 0}
          icon="🤖"
          color="from-blue-500 to-cyan-500"
          link="/agents"
        />
        <StatCard
          title="Total Tasks"
          value={stats?.totalTasks || 0}
          icon="📋"
          color="from-emerald-500 to-teal-500"
          link="/kanban"
        />
        <StatCard
          title="Completed"
          value={stats?.completedTasks || 0}
          icon="✅"
          color="from-violet-500 to-purple-500"
          link="/kanban"
        />
        <StatCard
          title="Tokens Used"
          value={formatNumber(stats?.totalTokens || 0)}
          icon="⚡"
          color="from-amber-500 to-orange-500"
        />
        <StatCard
          title="Monthly Spend"
          value={`$${(stats?.totalSpend || 0).toFixed(2)}`}
          icon="💰"
          color="from-green-500 to-emerald-500"
          link="/budget"
        />
        <StatCard
          title="Local Inferences"
          value={formatNumber(stats?.localInferences || 0)}
          icon="🏠"
          color="from-teal-500 to-cyan-500"
          link="/inference"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Tasks */}
        <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold">Recent Tasks</h3>
            <Link to="/kanban" className="text-indigo-400 hover:text-indigo-300 text-sm">
              View all →
            </Link>
          </div>
          
          <div className="space-y-3">
            {recentTasks.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No tasks yet</p>
            ) : (
              recentTasks.map((task) => (
                <div
                  key={task._id}
                  className="flex items-center gap-4 p-4 bg-[#0a0a0f] rounded-xl border border-[#2d2d3d] hover:border-indigo-500/50 transition-colors"
                >
                  <div className={`w-2 h-2 rounded-full ${getPriorityColor(task.priority)}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{task.title}</p>
                    <p className="text-sm text-gray-500">
                      {task.assignedTo?.name || 'Unassigned'} • {task.status}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-lg text-xs ${getStatusBadge(task.status)}`}>
                    {task.progress}%
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Agent Status */}
        <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold">Agent Status</h3>
            <Link to="/agents" className="text-indigo-400 hover:text-indigo-300 text-sm">
              Manage →
            </Link>
          </div>
          
          <div className="space-y-3">
            {agents.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No agents found</p>
            ) : (
              agents.slice(0, 5).map((agent) => (
                <div
                  key={agent._id}
                  className="flex items-center gap-4 p-4 bg-[#0a0a0f] rounded-xl border border-[#2d2d3d]"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-700 to-gray-600 flex items-center justify-center text-lg">
                    {agent.avatar || '🤖'}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{agent.name}</p>
                    <p className="text-sm text-gray-500">{agent.role}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${getAgentStatusColor(agent.status)}`} />
                    <span className="text-sm text-gray-400 capitalize">{agent.status}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-2xl p-6">
        <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-4">
          <QuickActionButton
            to="/kanban"
            icon="➕"
            label="New Task"
          />
          <QuickActionButton
            to="/agents"
            icon="🤖"
            label="Spawn Agent"
          />
          <QuickActionButton
            to="/chat"
            icon="💬"
            label="Team Chat"
          />
          <QuickActionButton
            to="/settings"
            icon="🔑"
            label="Add API Key"
          />
          <QuickActionButton
            to="/budget"
            icon="💰"
            label="Budget"
          />
          <QuickActionButton
            to="/connections"
            icon="🔌"
            label="Connections"
          />
        </div>
      </div>

      {/* Cost Savings Widget */}
      {budgetData && budgetData.savedAmount > 0 && (
        <div className="bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-500/30 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-emerald-400">Cost Savings</h3>
              <p className="text-gray-400 text-sm">
                Saved ${budgetData.savedAmount.toFixed(2)} this month by using local inference
                ({budgetData.localRequests} of {budgetData.totalRequests} requests handled locally)
              </p>
            </div>
            <Link to="/budget" className="text-emerald-400 hover:text-emerald-300 text-sm">
              View details →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper Components
const StatCard = ({ title, value, icon, color, link }) => {
  const content = (
    <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6 hover:border-indigo-500/30 transition-all group">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-400 text-sm mb-1">{title}</p>
          <p className="text-3xl font-bold">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center text-2xl group-hover:scale-110 transition-transform`}>
          {icon}
        </div>
      </div>
    </div>
  );

  if (link) {
    return <Link to={link}>{content}</Link>;
  }

  return content;
};

const QuickActionButton = ({ to, icon, label }) => (
  <Link
    to={to}
    className="flex items-center gap-3 px-6 py-3 bg-[#0a0a0f] hover:bg-indigo-500/20 border border-[#2d2d3d] hover:border-indigo-500/50 rounded-xl transition-all"
  >
    <span className="text-xl">{icon}</span>
    <span className="font-medium">{label}</span>
  </Link>
);

// Helper Functions
const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
};

const getPriorityColor = (priority) => {
  const colors = {
    low: 'bg-blue-500',
    medium: 'bg-yellow-500',
    high: 'bg-orange-500',
    urgent: 'bg-red-500'
  };
  return colors[priority] || 'bg-gray-500';
};

const getStatusBadge = (status) => {
  const badges = {
    'todo': 'bg-gray-500/20 text-gray-400',
    'in-progress': 'bg-blue-500/20 text-blue-400',
    'review': 'bg-yellow-500/20 text-yellow-400',
    'done': 'bg-green-500/20 text-green-400'
  };
  return badges[status] || 'bg-gray-500/20 text-gray-400';
};

const getAgentStatusColor = (status) => {
  const colors = {
    online: 'bg-green-500',
    busy: 'bg-yellow-500',
    offline: 'bg-gray-500',
    error: 'bg-red-500'
  };
  return colors[status] || 'bg-gray-500';
};

export default Dashboard;
